/**
 * ENT-6272: the Supabase client's `global.fetch`. A direct PostgREST request
 * (`supabase.from(...)`, `supabase.rpc(...)`) that leaves with an access token
 * which has just expired — a tab waking from sleep fires its reads before the
 * auth library's refresh timer catches up — is rejected `401 PGRST303
 * "JWT expired"`. That is not a failed read: this fetch hands the rejection to
 * the shared stale-token recovery (`recoverExpiredRestRequest`, the same
 * recovery `invokeProtectedFunction` uses), which refreshes the session once
 * for every concurrent caller and replays the request once.
 *
 * Replay rule — only requests that cannot change data are replayed:
 *   - GET and HEAD: PostgREST runs them in a READ ONLY transaction, including
 *     `rpc(fn, args, { get: true })`, so the server itself refuses a write;
 *   - POST `rpc/<fn>` only when `<fn>` is in READ_ONLY_RPC_REPLAY_ALLOWLIST.
 * Every other request (insert, update, upsert, delete, any other RPC) gets its
 * 401 back untouched, exactly as before: a non-idempotent write is never
 * replayed silently. Auth, Edge Function, Storage and Realtime traffic also
 * passes through this fetch and is never touched here.
 */
import * as Sentry from '@sentry/react';

export interface ExpiredJwtReplayRequest {
  /** `rest:<path>`, e.g. `rest:organization_members` or `rest:rpc/<fn>`. */
  label: string;
  rejected: Response;
  rejectedToken: string;
  /** Sends the same request again with `accessToken`, bypassing this fetch. */
  replay: (accessToken: string) => Promise<Response>;
  /**
   * False when the rejection could not be read as an expiry (a bodyless HEAD):
   * a replay still rejected after a successful refresh then returns its 401
   * instead of ending the session.
   */
  endSessionIfReplayRejected: boolean;
}

export type ExpiredJwtRecovery = (request: ExpiredJwtReplayRequest) => Promise<Response>;

interface ExpiredJwtReplayFetchOptions {
  /** `<SUPABASE_URL>/rest/v1/`: only requests under it are considered. */
  restUrl: string;
  recover: ExpiredJwtRecovery;
  baseFetch?: typeof fetch;
}

/**
 * RPCs called by POST that may be replayed after an expired-token rejection.
 * An entry MUST be declared STABLE or IMMUTABLE (Postgres refuses any write in a
 * non-volatile function), checked in live `pg_proc.provolatile` on staging and
 * production before it is added. A VOLATILE function never belongs here, even
 * if its body only reads today: nothing stops it writing tomorrow.
 *
 * All entries below: `provolatile = 's'`, one overload, on staging and
 * production, 2026-10-29 (ENT-6272: the Pitch Book page's reads).
 */
export const READ_ONLY_RPC_REPLAY_ALLOWLIST: ReadonlySet<string> = new Set([
  'can_create_pitch_book',
  'get_customer_briefing_evidence_bundles',
  'get_customer_briefings_dashboard_page',
  'get_my_pitch_book_access_requests',
  'get_pitch_book_access_requests',
  'get_pitch_book_check_people',
]);

const BEARER_PREFIX = 'Bearer ';

/** The PostgREST path (`organization_members`, `rpc/<fn>`) when the request may be replayed. */
function replayablePath(restUrl: string, input: RequestInfo | URL, init?: RequestInit): string | null {
  // supabase-js sends a URL string and an init; a Request object carries its own
  // single-use body, so it is never replayed.
  if (typeof input !== 'string' && !(input instanceof URL)) {
    return null;
  }
  const url = new URL(String(input));
  const rest = new URL(restUrl);
  if (url.origin !== rest.origin || !url.pathname.startsWith(rest.pathname)) {
    return null;
  }
  if (init?.body !== undefined && init.body !== null && typeof init.body !== 'string') {
    return null;
  }
  const path = url.pathname.slice(rest.pathname.length);
  const method = (init?.method ?? 'GET').toUpperCase();
  if (method === 'GET' || method === 'HEAD') {
    return path;
  }
  if (method === 'POST' && path.startsWith('rpc/') && READ_ONLY_RPC_REPLAY_ALLOWLIST.has(path.slice('rpc/'.length))) {
    return path;
  }
  return null;
}

type RejectionKind = 'expired' | 'unclassified';

/**
 * PostgREST's verdict on a token past its `exp`, not any other 401. PGRST303 is
 * every JWT-claims rejection ("JWT issued at future", "JWT not in audience", …):
 * a refresh cannot cure those, and a replay rejected again would end the
 * session, so only the expiry message qualifies.
 *
 * A HEAD response has no body, and the gateway does not expose the
 * `WWW-Authenticate` header that names the cause, so a HEAD 401 with a user
 * token is `unclassified`: it is refreshed and replayed once, but never ends
 * the session.
 */
async function classifyRejection(response: Response, method: string): Promise<RejectionKind | null> {
  if (method === 'HEAD') {
    return 'unclassified';
  }
  try {
    const body = await response.clone().json() as { message?: unknown };
    return typeof body.message === 'string' && /^jwt expired$/i.test(body.message) ? 'expired' : null;
  } catch {
    // An unreadable body is not proof of expiry: the 401 goes back untouched.
    return null;
  }
}

export function createExpiredJwtReplayFetch({
  restUrl,
  recover,
  baseFetch = (input, init) => fetch(input, init),
}: ExpiredJwtReplayFetchOptions): typeof fetch {
  return async (input, init) => {
    const response = await baseFetch(input, init);
    if (response.status !== 401) {
      return response;
    }
    const path = replayablePath(restUrl, input, init);
    const authorization = new Headers(init?.headers).get('Authorization');
    if (path === null || !authorization?.startsWith(BEARER_PREFIX)) {
      return response;
    }
    const kind = await classifyRejection(response, (init?.method ?? 'GET').toUpperCase());
    if (kind === null) {
      return response;
    }

    const label = `rest:${path}`;
    let replayFailure: unknown;
    try {
      return await recover({
        label,
        rejected: response,
        rejectedToken: authorization.slice(BEARER_PREFIX.length).trim(),
        endSessionIfReplayRejected: kind === 'expired',
        replay: async (accessToken) => {
          try {
            // A caller that gave up while the shared refresh ran gets its abort, not a replay.
            init?.signal?.throwIfAborted();
            const headers = new Headers(init?.headers);
            headers.set('Authorization', `${BEARER_PREFIX}${accessToken}`);
            return await baseFetch(input, { ...init, headers });
          } catch (error) {
            replayFailure = error;
            throw error;
          }
        },
      });
    } catch (error) {
      // The replay's own abort or network failure is the request's real outcome.
      if (error === replayFailure) {
        throw error;
      }
      // The recovery itself broke: hand back the original 401 (the outcome
      // before ENT-6272) and report why it was not recovered.
      Sentry.captureException(error, {
        tags: { auth_recovery: 'rest_replay_failed', function_name: label },
      });
      return response;
    }
  };
}
